// 原控制组件零修改：桥接层只读最新设备信息，手动操作仍使用原组件/原接口。
import { createAiClient, identity } from '@/ai/services/api'

let surfaceOwner = null
export function claimManualSurface(owner) { if(surfaceOwner && surfaceOwner !== owner)return false; surfaceOwner=owner;return true }
export function releaseManualSurface(owner) { if(surfaceOwner===owner)surfaceOwner=null }

export const MANUAL_TYPES = Object.freeze({
  '01': 'switch', '021': 'switch', '05': 'switch', '048': 'switch', '028': 'switch',
  '02': 'popup', '014': 'popup', '049': 'popup', '08': 'popup', '016': 'popup',
  '017': 'popup', '018': 'popup', '024': 'popup', '023': 'popup', '025': 'popup',
  '026': 'popup', '029': 'popup', '030': 'popup', '06': 'popup', '010': 'popup',
  '015': 'popup', '020': 'popup', '09': 'popup', '03': 'popup', '07': 'popup',
  '013': 'popup', '022': 'popup', '019': 'popup', '027': 'popup', '050': 'popup', '051': 'popup'
})

export function detachedDevice(device) { return JSON.parse(JSON.stringify(device)) }

// 原弹窗只在手动接口成功后更新objData；同步其确认记录，不监听乐观的userControlData。
// 保留AI卡片原产品类型（组设备弹窗存在类型别名），禁止跨设备/区域串写。
export function confirmedManualPreview(current, update) {
  if(!current || !update || !current.deviceId || !current.productId || !current.regionId)return null
  if(['deviceId','productId','regionId'].some(key=>current[key]!==update[key]))return null
  const next={...detachedDevice(current),...detachedDevice(update),productCode:current.productCode}
  return JSON.stringify(next)===JSON.stringify(current)?null:next
}

// 组调光协议参数与对应单设备一致。仅映射弹窗的组件选择，deviceId仍为原组设备。
export function popupDevice(device) {
  const copy = detachedDevice(device)
  if (copy.productCode === '050') copy.productCode = '025'
  if (copy.productCode === '051') copy.productCode = '026'
  return copy
}

// 仅合并正在进行的读取，不缓存已完成状态。最多4个详情请求，避免长对话同时加载压垮接口。
const pendingReads = new Map()
const readQueue = []
const MAX_READS = 4
let activeReads = 0

function forgetRead(job) {
  if (pendingReads.get(job.key) === job) pendingReads.delete(job.key)
}
function pumpReads() {
  while (activeReads < MAX_READS && readQueue.length) {
    const job = readQueue.shift()
    if (!job.users.size || job.scope !== identity()) {
      forgetRead(job)
      job.reject(Error('ACCOUNT_CHANGED'))
      continue
    }
    activeReads++
    const finish = () => {
      activeReads--
      forgetRead(job)
      if (job.client) job.client.close()
      pumpReads()
    }
    try {
      job.client = createAiClient()
      Promise.resolve(job.client.post('device/get', { ref: job.ref })).then(
        result => { job.resolve(result); finish() },
        error => { job.reject(error); finish() }
      )
    } catch (error) { job.reject(error); finish() }
  }
}

export function createManualDeviceReader() {
  const scope = identity(), leases = new Set()
  let closed = false
  return {
    async get(ref, { fresh = false } = {}) {
      if (closed || scope !== identity()) throw Error('ACCOUNT_CHANGED')
      const snapshot = detachedDevice(ref)
      const key = JSON.stringify([scope, snapshot.regionId, snapshot.productId, snapshot.deviceId || null, snapshot.deviceId ? null : snapshot.title])
      // 控制后的读取必须新建，不能复用控制前尚未返回的请求。
      let job = fresh ? null : pendingReads.get(key)
      if (!job) {
        job = { key, scope, ref: snapshot, users: new Set(), client: null }
        job.promise = new Promise((resolve, reject) => { job.resolve = resolve; job.reject = reject })
        pendingReads.set(key, job)
        readQueue.push(job)
      }
      const lease = { job }
      leases.add(lease); job.users.add(lease)
      pumpReads()
      try {
        const result = await job.promise
        if (closed || scope !== identity()) throw Error('ACCOUNT_CHANGED')
        if (!result || !result.productCode || result.regionId !== snapshot.regionId || result.productId !== snapshot.productId ||
            (snapshot.deviceId && result.deviceId !== snapshot.deviceId)) throw Error('DEVICE_QUERY_FAILED')
        return detachedDevice(result)
      } finally { leases.delete(lease); job.users.delete(lease) }
    },
    valid: () => !closed && scope === identity(),
    close() {
      closed = true
      for (const lease of leases) {
        lease.job.users.delete(lease)
        // 一个卡片离开，不能取消仍被其他卡片使用的共享读取。
        if (!lease.job.users.size) {
          forgetRead(lease.job)
          if (lease.job.client) lease.job.client.close()
        }
      }
      leases.clear()
      pumpReads()
    }
  }
}
